import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Watches the user's onboarding bonus bucket and fires friendly milestone
 * toasts as it depletes. Threshold-crossing detection uses localStorage so we
 * don't re-show the same toast on remounts/refreshes.
 *
 * Milestones (only fired the FIRST time the bonus drops at/below the level):
 *   • 50% remaining (125 cr default) — "Has usado la mitad de tu bonus..."
 *   • 25% remaining (62 cr default)  — "Casi agotado..."
 *   • 0  remaining                   — "Bonus consumido" + CTA "Ver planes"
 */
export function useBonusMilestoneToasts(subscription) {
  const lastShownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!subscription?.onboarding_credits_total) return;
    const total = subscription.onboarding_credits_total || 0;
    if (total <= 0) return;
    const remaining = subscription.onboarding_credits_remaining
      ?? Math.max(0, total - (subscription.onboarding_credits_used || 0));
    const pct = remaining / total;
    const subId = subscription.subscription_id;
    if (!subId) return;

    const storageKey = `bonus_milestones_${subId}`;
    const seen = JSON.parse(localStorage.getItem(storageKey) || "[]");

    const milestones = [
      {
        id: "half",
        match: pct <= 0.5 && pct > 0.25,
        title: "Has usado la mitad de tu bonus de bienvenida",
        description: "Tu carta y web ya están tomando forma. ¡Sigue así!",
        emoji: "🎯",
      },
      {
        id: "quarter",
        match: pct <= 0.25 && pct > 0,
        title: "Casi agotado tu bonus de bienvenida",
        description: `Te quedan ${remaining} créditos. Después usarás tus créditos mensuales.`,
        emoji: "⚡",
      },
      {
        id: "depleted",
        match: pct <= 0,
        title: "Bonus de bienvenida consumido",
        description: "Los próximos consumos vienen de tus créditos mensuales (30/mes).",
        emoji: "✨",
      },
    ];

    for (const m of milestones) {
      if (m.match && !seen.includes(m.id) && lastShownRef.current !== m.id) {
        const opts = {
          description: m.description,
          duration: m.id === "depleted" ? 12000 : 7000,
        };
        if (m.id === "depleted") {
          opts.action = {
            label: "Ver planes",
            onClick: () => navigate("/app/subscription"),
          };
        }
        toast.success(`${m.emoji} ${m.title}`, opts);
        lastShownRef.current = m.id;
        const updated = [...seen, m.id];
        localStorage.setItem(storageKey, JSON.stringify(updated));
        break;
      }
    }
  }, [
    subscription?.subscription_id,
    subscription?.onboarding_credits_remaining,
    subscription?.onboarding_credits_used,
    subscription?.onboarding_credits_total,
    navigate,
  ]);
}
